// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Palette, Plus, Save, ExternalLink, Monitor, Smartphone, Tablet, Copy, Eye, RefreshCw,
  Upload, Trash2, Globe, X, Sparkles, ShieldCheck, Gauge, CheckCircle2, AlertTriangle,
  Download, Wand2, Contrast, Search, Filter, LayoutGrid, ImageIcon, Type as TypeIcon,
  Rocket, GitBranch, Building2, Mail, FileCode2,
} from "lucide-react";
import {
  listTenantBrandings, upsertTenantBranding, uploadBrandAsset, toEffective,
  type TenantBranding,
} from "@/lib/tenant-branding";
import { DomainVerificationPanel } from "./domain-verification-panel";
import { BrandingAuditPanel } from "./branding-audit-panel";

const empty: TenantBranding = {
  id: "", slug: "", name: "", tagline: "Education Operating System", descriptor: "",
  company: "", city: "Kampala", country: "Uganda", domain: "", custom_domains: [],
  support_email: "", feature_prefix: "", logo_url: null, hero_image_url: null,
  primary_color: "#0f3d2e", accent_color: "#c9a84c",
  hero_eyebrow: "", hero_headline: "One platform for every |school day.|", hero_subtitle: "",
  modules_json: [], module_graphics_json: {}, pricing_json: [], compliance_json: [],
  seo_title: "", seo_description: "", is_published: false,
};

const DEFAULT_MODULE_LIBRARY = [
  { slug: "wallets", icon: "Wallet", title: "Wallets", body: "Multi-guardian funding, daily allowances, category locks and emergency reserves." },
  { slug: "fees", icon: "GraduationCap", title: "Fees", body: "Tuition, boarding, PTA, trips and exam payments with installments and receipts." },
  { slug: "transport", icon: "Bus", title: "Transport", body: "Live GPS, tap-to-board, pickup authorization, route optimization and parent alerts." },
  { slug: "health", icon: "Heart", title: "Health Bay", body: "Allergies, sick-bay visits, medication compliance, vaccination records and alerts." },
  { slug: "marketplace", icon: "ShoppingBag", title: "Marketplace", body: "Vetted vendors for uniforms, books and supplies with parent approval workflows." },
  { slug: "identity", icon: "QrCode", title: "Identity", body: "Digital student passport for gate, dorm, canteen, library and transport activity." },
  { slug: "copilot", icon: "Brain", title: "Copilot", body: "Natural-language insights and actions for parents, students, schools and merchants." },
  { slug: "compliance", icon: "ShieldCheck", title: "Compliance", body: "Tenant-scoped audit trail, exports, controls and operating evidence." },
];

/* ────────────── color utilities ────────────── */
function hexToRgb(hex: string) {
  const m = /^#?([a-f0-9]{6})$/i.exec(hex.trim()); if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16); return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r: number, g: number, b: number) {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}
function tint(hex: string, pct: number) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * pct, g + (255 - g) * pct, b + (255 - b) * pct);
}
function shade(hex: string, pct: number) {
  const { r, g, b } = hexToRgb(hex); return rgbToHex(r * (1 - pct), g * (1 - pct), b * (1 - pct));
}
function luminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const a = [r, g, b].map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}
function contrastRatio(a: string, b: string) {
  const [L1, L2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (L1 + 0.05) / (L2 + 0.05);
}
function harmonize(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  // simple complementary-ish accent shift
  return rgbToHex(255 - r * 0.4, 255 - g * 0.6, 255 - b * 0.9);
}

export function TenantBrandingConsole() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["tenant_brandings"], queryFn: listTenantBrandings,
  });

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [draft, setDraft] = useState<TenantBranding | null>(null);
  const [device, setDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "live" | "draft" | "domain">("all");
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => { if (!selectedSlug && rows.length) setSelectedSlug(rows[0].slug); }, [rows, selectedSlug]);
  useEffect(() => {
    if (!selectedSlug) { setDraft(null); return; }
    const row = rows.find((r) => r.slug === selectedSlug);
    if (row) setDraft({ ...row });
  }, [selectedSlug, rows]);

  useEffect(() => {
    if (!draft || !iframeRef.current) return;
    const t = setTimeout(() => iframeRef.current?.contentWindow?.postMessage(
      { type: "tricampus:brand-preview", row: draft }, "*",
    ), 60);
    return () => clearTimeout(t);
  }, [draft]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (q && !`${r.name} ${r.slug} ${r.domain}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "live") return r.is_published;
    if (filter === "draft") return !r.is_published;
    if (filter === "domain") return !!r.domain || (r.custom_domains?.length ?? 0) > 0;
    return true;
  }), [rows, q, filter]);

  const stats = useMemo(() => {
    const total = rows.length;
    const live = rows.filter((r) => r.is_published).length;
    const domained = rows.filter((r) => r.domain || (r.custom_domains?.length ?? 0) > 0).length;
    const withLogo = rows.filter((r) => r.logo_url).length;
    return { total, live, domained, withLogo };
  }, [rows]);

  const setField = <K extends keyof TenantBranding>(k: K, v: TenantBranding[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const startNew = () => {
    const slug = `tenant-${Math.random().toString(36).slice(2, 7)}`;
    const row = { ...empty, slug, name: "New tenant" };
    setDraft(row); setSelectedSlug(slug);
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.slug || !draft.name) { toast.error("Slug and name are required."); return; }
    setSaving(true);
    try {
      const { id: _id, updated_at: _u, ...payload } = draft as TenantBranding & { updated_at?: string };
      void _id; void _u;
      await upsertTenantBranding(payload as Partial<TenantBranding> & { slug: string });
      toast.success(`Saved ${draft.name}`);
      qc.invalidateQueries({ queryKey: ["tenant_brandings"] });
    } catch (e) { toast.error("Save failed", { description: (e as Error).message }); }
    finally { setSaving(false); }
  };

  const previewPath = draft ? `/preview/${encodeURIComponent(draft.slug)}` : "/";
  const bestDomain = draft
    ? (draft.domain?.trim() || (draft.custom_domains ?? []).find((d) => !!d) || "")
    : "";
  const openUrl = draft
    ? draft.is_published && bestDomain
      ? `https://${bestDomain.replace(/^https?:\/\//, "")}?tenant=${encodeURIComponent(draft.slug)}`
      : `${previewPath}`
    : "/";

  const loadDefaultModules = () => {
    if (!draft) return;
    const prefix = draft.slug === "fundred" ? "Fundred " : "";
    setField("modules_json", DEFAULT_MODULE_LIBRARY.map((m) => ({ ...m, title: `${prefix}${m.title}` })) as never);
    toast.success("Modules loaded");
  };

  /* ────────────── brand health ────────────── */
  const health = useMemo(() => {
    if (!draft) return null;
    const checks = [
      { key: "identity", label: "Identity set", ok: !!(draft.name && draft.tagline && draft.feature_prefix) },
      { key: "logo",     label: "Logo uploaded", ok: !!draft.logo_url },
      { key: "hero_img", label: "Hero image", ok: !!draft.hero_image_url },
      { key: "hero_copy",label: "Hero copy", ok: !!(draft.hero_headline && draft.hero_subtitle) },
      { key: "modules",  label: "Modules configured", ok: (draft.modules_json?.length ?? 0) >= 3 },
      { key: "pricing",  label: "Pricing tiers", ok: (draft.pricing_json?.length ?? 0) >= 1 },
      { key: "domain",   label: "Domain assigned", ok: !!(draft.domain || (draft.custom_domains?.length ?? 0) > 0) },
      { key: "seo",      label: "SEO meta", ok: !!(draft.seo_title && draft.seo_description) },
      { key: "support",  label: "Support email", ok: /.+@.+\..+/.test(draft.support_email) },
      { key: "contrast", label: "WCAG AA contrast", ok: contrastRatio(draft.primary_color, "#ffffff") >= 4.5 },
    ];
    const passed = checks.filter((c) => c.ok).length;
    const score = Math.round((passed / checks.length) * 100);
    return { checks, score, passed, total: checks.length };
  }, [draft]);

  /* ────────────── brand kit export ────────────── */
  const exportKit = () => {
    if (!draft) return;
    const kit = {
      name: draft.name, slug: draft.slug, updated_at: new Date().toISOString(),
      colors: {
        primary: draft.primary_color, accent: draft.accent_color,
        primary_tints: [0.1, 0.2, 0.4, 0.6, 0.8].map((p) => tint(draft.primary_color, p)),
        primary_shades: [0.1, 0.2, 0.4, 0.6].map((p) => shade(draft.primary_color, p)),
      },
      assets: { logo: draft.logo_url, hero: draft.hero_image_url, modules: draft.module_graphics_json },
      voice: { tagline: draft.tagline, eyebrow: draft.hero_eyebrow, headline: draft.hero_headline, subtitle: draft.hero_subtitle },
      seo: { title: draft.seo_title, description: draft.seo_description },
      domains: [draft.domain, ...(draft.custom_domains ?? [])].filter(Boolean),
      support_email: draft.support_email,
    };
    const blob = new Blob([JSON.stringify(kit, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${draft.slug}-brand-kit.json`; a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Brand kit exported");
  };

  return (
    <div className="space-y-4">
      {/* ═══ HERO STRIP ═══ */}
      <Card
        className="relative overflow-hidden border-0 p-6 text-white"
        style={{ background: "linear-gradient(120deg, #0b2a1f 0%, #123f2f 40%, #1a5942 100%)" }}
      >
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,168,76,.5) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(64,224,168,.35) 0, transparent 45%)",
        }} />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/60">
              <Sparkles className="h-3 w-3" /> TriSphere white-label workshop
            </div>
            <div className="mt-2 font-display text-3xl leading-tight">
              Ship an entire education OS under any brand.
            </div>
            <div className="mt-1 max-w-xl text-sm text-white/70">
              Full-stack branding — identity, palette, typography voice, hero, modules, pricing, custom domains,
              SEO, legal copy, mobile icons and live per-tenant previews.
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 text-center">
            <HeroStat icon={LayoutGrid} label="Tenants" value={stats.total} />
            <HeroStat icon={Rocket}     label="Live"    value={stats.live} accent="#40e0a8" />
            <HeroStat icon={Globe}      label="Domains" value={stats.domained} />
            <HeroStat icon={ImageIcon}  label="Branded" value={stats.withLogo} accent="#c9a84c" />
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        {/* ═══ LEFT RAIL ═══ */}
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 font-display text-base">
              <Palette className="h-4 w-4 text-primary" /> Tenants
            </div>
            <Button size="sm" variant="outline" onClick={startNew}>
              <Plus className="mr-1 h-3.5 w-3.5" /> New
            </Button>
          </div>

          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand, slug, domain…" className="h-8 pl-7 text-xs" />
          </div>
          <div className="mb-3 flex flex-wrap gap-1 text-[10px]">
            {(["all","live","draft","domain"] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`rounded-full border px-2 py-0.5 uppercase tracking-widest transition ${
                  filter === f ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"
                }`}>
                {f === "domain" ? "with domain" : f}
              </button>
            ))}
            <span className="ml-auto flex items-center gap-1 text-muted-foreground"><Filter className="h-3 w-3" />{filtered.length}</span>
          </div>

          {isLoading ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : (
            <div className="max-h-[560px] space-y-1.5 overflow-y-auto pr-1">
              {filtered.map((r) => {
                const isSel = selectedSlug === r.slug;
                const hasDomain = !!(r.domain || (r.custom_domains?.length ?? 0) > 0);
                return (
                  <button key={r.slug} onClick={() => setSelectedSlug(r.slug)}
                    className={`group flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition ${
                      isSel ? "border-primary bg-primary/5 shadow-sm" : "hover:bg-muted/50"
                    }`}>
                    <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md shadow-sm"
                      style={{ background: `linear-gradient(135deg, ${r.primary_color}, ${r.accent_color})` }}>
                      {r.logo_url && <img src={r.logo_url} alt="" className="h-full w-full object-contain p-1" />}
                    </span>
                    <div className="flex-1 leading-tight">
                      <div className="text-sm font-medium">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {r.slug} · {r.domain || "no domain"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      {r.is_published
                        ? <Badge variant="secondary" className="bg-emerald-500/15 px-1.5 py-0 text-[9px] text-emerald-700">Live</Badge>
                        : <Badge variant="outline" className="px-1.5 py-0 text-[9px]">Draft</Badge>}
                      {hasDomain && <Globe className="h-3 w-3 text-muted-foreground" />}
                    </div>
                  </button>
                );
              })}
              {!filtered.length && (
                <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                  No tenants match.
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ═══ RIGHT PANE ═══ */}
        <div className="space-y-4">
          {!draft ? (
            <Card className="grid h-64 place-items-center text-sm text-muted-foreground">
              Select a tenant to edit its branding.
            </Card>
          ) : (
            <>
              {/* Header bar */}
              <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-3">
                  <span className="h-12 w-12 overflow-hidden rounded-xl shadow-sm ring-1 ring-border"
                    style={{ background: `linear-gradient(135deg, ${draft.primary_color}, ${draft.accent_color})` }}>
                    {draft.logo_url && <img src={draft.logo_url} alt="" className="h-full w-full object-contain p-1" />}
                  </span>
                  <div>
                    <div className="font-display text-lg leading-none">{draft.name || "Untitled tenant"}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      <GitBranch className="h-3 w-3" /> /preview/{draft.slug}
                      {draft.updated_at && <span>· updated {new Date(draft.updated_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  {health && <HealthPill score={health.score} />}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={exportKit}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Brand kit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    void navigator.clipboard.writeText(openUrl.startsWith("http") ? openUrl : window.location.origin + openUrl);
                    toast.success("Preview URL copied");
                  }}>
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={openUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open
                    </a>
                  </Button>
                  <div className="mx-1 flex items-center gap-2 rounded-full border bg-card px-2 py-1 text-xs">
                    <span className="text-muted-foreground">Publish</span>
                    <Switch checked={draft.is_published} onCheckedChange={(v) => setField("is_published", v)} />
                  </div>
                  <Button size="sm" onClick={save} disabled={saving}>
                    <Save className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </Card>

              <div className="grid gap-4 xl:grid-cols-[minmax(0,460px)_1fr]">
                {/* Editor tabs */}
                <Card className="p-4">
                  <Tabs defaultValue="overview">
                    <TabsList className="grid grid-cols-5">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="brand">Identity</TabsTrigger>
                      <TabsTrigger value="palette">Palette</TabsTrigger>
                      <TabsTrigger value="hero">Hero</TabsTrigger>
                      <TabsTrigger value="modules">Modules</TabsTrigger>
                    </TabsList>
                    <TabsList className="mt-1 grid grid-cols-5">
                      <TabsTrigger value="pricing">Pricing</TabsTrigger>
                      <TabsTrigger value="domains">Domains</TabsTrigger>
                      <TabsTrigger value="seo">SEO</TabsTrigger>
                      <TabsTrigger value="legal">Legal</TabsTrigger>
                      <TabsTrigger value="audit">Audit</TabsTrigger>
                    </TabsList>

                    {/* ─── OVERVIEW ─── */}
                    <TabsContent value="overview" className="mt-4 space-y-4">
                      {health && (
                        <>
                          <div className="rounded-xl border bg-gradient-to-br from-muted/30 to-transparent p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Brand readiness</div>
                                <div className="mt-1 flex items-baseline gap-2">
                                  <span className="font-display text-4xl">{health.score}</span>
                                  <span className="text-xs text-muted-foreground">/ 100</span>
                                </div>
                                <div className="text-xs text-muted-foreground">{health.passed} of {health.total} checks passing</div>
                              </div>
                              <Gauge className="h-14 w-14 text-primary/40" />
                            </div>
                            <Progress value={health.score} className="mt-3 h-1.5" />
                          </div>
                          <div className="space-y-1.5">
                            {health.checks.map((c) => (
                              <div key={c.key} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs">
                                {c.ok
                                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                  : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                                <span className="flex-1">{c.label}</span>
                                <span className={`text-[10px] uppercase tracking-widest ${c.ok ? "text-emerald-600" : "text-amber-600"}`}>
                                  {c.ok ? "OK" : "todo"}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="rounded-lg border bg-card p-3">
                            <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                              <Rocket className="h-3 w-3" /> Deployment checklist
                            </div>
                            <ul className="space-y-1 text-xs text-muted-foreground">
                              <li>· All identity fields set → verify voice tone matches customer segment</li>
                              <li>· Domains resolve & DNS verified → SSL auto-provisioned</li>
                              <li>· Legal/compliance copy reviewed for jurisdiction</li>
                              <li>· Publish toggle flipped → cache invalidation runs within 60s</li>
                            </ul>
                          </div>
                        </>
                      )}
                    </TabsContent>

                    {/* ─── IDENTITY ─── */}
                    <TabsContent value="brand" className="mt-4 space-y-3">
                      <Field label="Slug (URL key)" required>
                        <Input value={draft.slug} onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
                      </Field>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Product name"><Input value={draft.name} onChange={(e) => setField("name", e.target.value)} /></Field>
                        <Field label="Feature prefix"><Input value={draft.feature_prefix} onChange={(e) => setField("feature_prefix", e.target.value)} placeholder="e.g. TriSphere" /></Field>
                      </div>
                      <Field label="Tagline"><Input value={draft.tagline} onChange={(e) => setField("tagline", e.target.value)} /></Field>
                      <Field label="Descriptor"><Textarea rows={2} value={draft.descriptor} onChange={(e) => setField("descriptor", e.target.value)} /></Field>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Company"><Input value={draft.company} onChange={(e) => setField("company", e.target.value)} /></Field>
                        <Field label="Support email"><Input value={draft.support_email} onChange={(e) => setField("support_email", e.target.value)} /></Field>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="City"><Input value={draft.city} onChange={(e) => setField("city", e.target.value)} /></Field>
                        <Field label="Country"><Input value={draft.country} onChange={(e) => setField("country", e.target.value)} /></Field>
                        <Field label="Primary domain"><Input value={draft.domain} onChange={(e) => setField("domain", e.target.value)} placeholder="tricampus.live" /></Field>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Logo (auto 512²)">
                          <ImageUpload slug={draft.slug} kind="logo" value={draft.logo_url} onChange={(url) => setField("logo_url", url)} previewClass="h-16 w-16 rounded-lg border object-contain bg-white" />
                        </Field>
                        <Field label="Hero image (1600×1000)">
                          <ImageUpload slug={draft.slug} kind="hero" value={draft.hero_image_url} onChange={(url) => setField("hero_image_url", url)} previewClass="h-16 w-28 rounded-lg border object-cover" />
                        </Field>
                      </div>
                    </TabsContent>

                    {/* ─── PALETTE ─── */}
                    <TabsContent value="palette" className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Primary color"><ColorInput value={draft.primary_color} onChange={(v) => setField("primary_color", v)} /></Field>
                        <Field label="Accent color"><ColorInput value={draft.accent_color} onChange={(v) => setField("accent_color", v)} /></Field>
                      </div>
                      <Button size="sm" variant="outline" className="w-full" onClick={() => setField("accent_color", harmonize(draft.primary_color))}>
                        <Wand2 className="mr-1.5 h-3.5 w-3.5" /> Auto-harmonize accent from primary
                      </Button>

                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Primary — tints</Label>
                        <div className="mt-1.5 grid grid-cols-6 overflow-hidden rounded-md border">
                          {[0, 0.15, 0.3, 0.5, 0.7, 0.85].map((p) => {
                            const c = tint(draft.primary_color, p);
                            return <Swatch key={`t${p}`} color={c} label={`+${Math.round(p*100)}%`} />;
                          })}
                        </div>
                        <Label className="mt-3 block text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Primary — shades</Label>
                        <div className="mt-1.5 grid grid-cols-6 overflow-hidden rounded-md border">
                          {[0, 0.15, 0.3, 0.45, 0.6, 0.75].map((p) => {
                            const c = shade(draft.primary_color, p);
                            return <Swatch key={`s${p}`} color={c} label={`-${Math.round(p*100)}%`} dark />;
                          })}
                        </div>
                      </div>

                      <div className="rounded-lg border p-3">
                        <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          <Contrast className="h-3 w-3" /> WCAG contrast
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <ContrastCell fg="#ffffff" bg={draft.primary_color} label="Primary / White" />
                          <ContrastCell fg={draft.primary_color} bg="#ffffff" label="White / Primary" />
                          <ContrastCell fg="#ffffff" bg={draft.accent_color} label="Accent / White" />
                          <ContrastCell fg={draft.primary_color} bg={draft.accent_color} label="Primary / Accent" />
                        </div>
                      </div>

                      <div className="rounded-lg border p-3">
                        <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Live component preview</div>
                        <div className="space-y-2">
                          <button className="rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm"
                            style={{ background: `linear-gradient(135deg, ${draft.primary_color}, ${shade(draft.primary_color, 0.15)})` }}>
                            Pay school fees
                          </button>
                          <div className="rounded-md border p-2 text-xs" style={{ borderColor: draft.accent_color }}>
                            <span className="font-semibold" style={{ color: draft.primary_color }}>Term 2 invoice</span> —
                            <span className="ml-1" style={{ color: draft.accent_color }}>UGX 1,250,000</span>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* ─── HERO ─── */}
                    <TabsContent value="hero" className="mt-4 space-y-3">
                      <Field label="Eyebrow"><Input value={draft.hero_eyebrow} onChange={(e) => setField("hero_eyebrow", e.target.value)} /></Field>
                      <Field label="Headline (wrap gradient text in |pipes|)">
                        <Textarea rows={2} value={draft.hero_headline} onChange={(e) => setField("hero_headline", e.target.value)} />
                      </Field>
                      <Field label="Subtitle">
                        <Textarea rows={4} value={draft.hero_subtitle} onChange={(e) => setField("hero_subtitle", e.target.value)} />
                      </Field>
                      <div className="rounded-lg border p-3">
                        <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          <TypeIcon className="h-3 w-3" /> Voice preview
                        </div>
                        <div className="rounded-md p-3 text-white" style={{
                          background: `linear-gradient(120deg, ${shade(draft.primary_color, 0.2)}, ${draft.primary_color})`,
                        }}>
                          {draft.hero_eyebrow && <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">{draft.hero_eyebrow}</div>}
                          <div className="mt-1 font-display text-xl leading-tight">
                            {draft.hero_headline.split("|").map((seg, i) => i % 2 === 1
                              ? <span key={i} style={{ color: draft.accent_color }}>{seg}</span>
                              : <span key={i}>{seg}</span>)}
                          </div>
                          <div className="mt-1 text-xs opacity-80">{draft.hero_subtitle}</div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* ─── MODULES ─── */}
                    <TabsContent value="modules" className="mt-4 space-y-4">
                      <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3">
                        <div>
                          <div className="text-sm font-medium">Tenant module suite</div>
                          <div className="text-xs text-muted-foreground">Load the standard TriSphere module library, then edit names and copy per tenant.</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={loadDefaultModules}>
                          <Sparkles className="mr-1 h-3.5 w-3.5" /> Load modules
                        </Button>
                      </div>
                      <JsonEditor value={draft.modules_json} onChange={(v) => setField("modules_json", v as never)}
                        label="Modules" placeholder='[{"slug":"wallets","icon":"Wallet","title":"Wallets","body":"…"}]' />
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Module graphics</Label>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {(draft.modules_json ?? []).map((m: any) => (
                            <div key={m.slug} className="flex items-center gap-2 rounded-md border p-2">
                              <ImageUpload slug={draft.slug} kind="module" subKey={m.slug}
                                value={(draft.module_graphics_json ?? {})[m.slug] ?? null}
                                onChange={(url) => setField("module_graphics_json", { ...(draft.module_graphics_json ?? {}), [m.slug]: url ?? "" } as never)}
                                previewClass="h-12 w-12 rounded object-cover border" />
                              <div className="min-w-0 flex-1 text-xs">
                                <div className="truncate font-medium">{m.title}</div>
                                <div className="truncate text-[10px] text-muted-foreground">{m.slug}</div>
                              </div>
                            </div>
                          ))}
                          {(!draft.modules_json || !draft.modules_json.length) && (
                            <div className="col-span-2 text-xs text-muted-foreground">Add modules above to attach graphics.</div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="pricing" className="mt-4">
                      <JsonEditor value={draft.pricing_json} onChange={(v) => setField("pricing_json", v as never)}
                        label="Pricing tiers" placeholder='[{"name":"Community","forWho":"…","price":150000,"sub":"…","perks":["…"]}]' />
                    </TabsContent>

                    <TabsContent value="domains" className="mt-4 space-y-3">
                      <Field label="Primary domain">
                        <Input value={draft.domain} onChange={(e) => setField("domain", e.target.value)} placeholder="tricampus.live" />
                      </Field>
                      <DomainVerificationPanel slug={draft.slug} primaryDomain={draft.domain}
                        customDomains={draft.custom_domains ?? []}
                        onCustomDomainsChange={(v) => setField("custom_domains", v as never)} />
                    </TabsContent>

                    <TabsContent value="seo" className="mt-4 space-y-3">
                      <Field label="SEO title"><Input value={draft.seo_title} onChange={(e) => setField("seo_title", e.target.value)} /></Field>
                      <Field label="SEO description"><Textarea rows={3} value={draft.seo_description} onChange={(e) => setField("seo_description", e.target.value)} /></Field>
                      <div className="rounded-lg border bg-card p-3">
                        <div className="mb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          <Search className="mr-1 inline h-3 w-3" /> Google preview
                        </div>
                        <div className="text-[11px] text-emerald-700">{draft.domain || "tricampus.live"}</div>
                        <div className="text-sm text-blue-800 hover:underline">{draft.seo_title || `${draft.name} — ${draft.tagline}`}</div>
                        <div className="text-[12px] text-muted-foreground">{draft.seo_description || draft.descriptor || "…"}</div>
                      </div>
                    </TabsContent>

                    <TabsContent value="legal" className="mt-4 space-y-3">
                      <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2 text-[11px] text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                        Uganda payments law: define e-money issuer, KYC & 10-yr record retention copy here.
                      </div>
                      <JsonEditor value={draft.compliance_json} onChange={(v) => setField("compliance_json", v as never)}
                        label="Compliance badges" placeholder='[{"icon":"ShieldCheck","title":"…","body":"…"}]' />
                    </TabsContent>

                    <TabsContent value="audit" className="mt-4"><BrandingAuditPanel tenantSlug={draft.slug} /></TabsContent>
                  </Tabs>
                </Card>

                {/* Live preview */}
                <Card className="overflow-hidden">
                  <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Eye className="h-3.5 w-3.5" />
                      Live preview — <span className="font-medium">{toEffective(draft).name}</span>
                      <Badge variant="outline" className="ml-1 px-1.5 py-0 text-[9px]">
                        {device === "desktop" ? "1280px" : device === "tablet" ? "820px" : "390px"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant={device === "desktop" ? "default" : "ghost"} onClick={() => setDevice("desktop")}><Monitor className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant={device === "tablet" ? "default" : "ghost"} onClick={() => setDevice("tablet")}><Tablet className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant={device === "mobile" ? "default" : "ghost"} onClick={() => setDevice("mobile")}><Smartphone className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => iframeRef.current?.contentWindow?.location.reload()}><RefreshCw className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <div className="grid place-items-center bg-[radial-gradient(circle_at_center,hsl(var(--muted))_1px,transparent_1px)] [background-size:16px_16px] p-6">
                    <div className="overflow-hidden rounded-2xl border bg-background shadow-xl transition-all"
                      style={{
                        width: device === "desktop" ? "100%" : device === "tablet" ? 820 : 390,
                        maxWidth: "100%",
                        height: device === "desktop" ? 720 : device === "tablet" ? 900 : 780,
                      }}>
                      <iframe ref={iframeRef} title="Landing preview" src={previewPath} className="h-full w-full"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────── UI primitives ────────────── */

function HeroStat({ icon: Icon, label, value, accent = "#ffffff" }: { icon: any; label: string; value: number; accent?: string }) {
  return (
    <div className="min-w-[84px] rounded-xl border border-white/15 bg-white/5 px-3 py-2 backdrop-blur">
      <Icon className="mx-auto h-4 w-4" style={{ color: accent }} />
      <div className="mt-1 text-center font-display text-2xl leading-none">{value}</div>
      <div className="mt-0.5 text-center text-[9px] uppercase tracking-widest text-white/60">{label}</div>
    </div>
  );
}

function HealthPill({ score }: { score: number }) {
  const tone = score >= 85 ? "emerald" : score >= 60 ? "amber" : "rose";
  const styles: Record<string, string> = {
    emerald: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    amber:   "bg-amber-500/10 text-amber-700 border-amber-500/30",
    rose:    "bg-rose-500/10 text-rose-700 border-rose-500/30",
  };
  return (
    <span className={`ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${styles[tone]}`}>
      <Gauge className="h-3 w-3" /> {score}% ready
    </span>
  );
}

function Swatch({ color, label, dark }: { color: string; label: string; dark?: boolean }) {
  return (
    <div className="grid h-12 place-items-center" style={{ background: color }}>
      <span className={`text-[9px] font-medium uppercase tracking-widest ${dark ? "text-white/80" : "text-black/70"}`}>{label}</span>
    </div>
  );
}

function ContrastCell({ fg, bg, label }: { fg: string; bg: string; label: string }) {
  const ratio = contrastRatio(fg, bg);
  const grade = ratio >= 7 ? "AAA" : ratio >= 4.5 ? "AA" : ratio >= 3 ? "AA lg" : "Fail";
  const ok = ratio >= 4.5;
  return (
    <div className="overflow-hidden rounded-md border">
      <div className="grid h-10 place-items-center text-xs font-medium" style={{ background: bg, color: fg }}>Aa · {ratio.toFixed(2)}</div>
      <div className="flex items-center justify-between px-2 py-1 text-[10px]">
        <span className="text-muted-foreground">{label}</span>
        <span className={ok ? "font-semibold text-emerald-600" : "font-semibold text-rose-600"}>{grade}</span>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      {label && (
        <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      {children}
    </div>
  );
}

function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2 rounded-md border p-1">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-10 cursor-pointer rounded-sm border-0 bg-transparent" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 border-0 shadow-none" />
    </div>
  );
}

function JsonEditor({ value, onChange, label, placeholder }:
  { value: unknown; onChange: (v: unknown) => void; label: string; placeholder?: string }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? [], null, 2));
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setText(JSON.stringify(value ?? [], null, 2)); }, [value]);
  return (
    <div className="space-y-1">
      {label && (
        <Label className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          <FileCode2 className="h-3 w-3" /> {label}
        </Label>
      )}
      <Textarea rows={12} value={text} placeholder={placeholder} className="font-mono text-xs"
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value)); setErr(null); }
          catch (ex) { setErr((ex as Error).message); }
        }} />
      {err && <div className="text-xs text-destructive">{err}</div>}
    </div>
  );
}

function ImageUpload({ slug, kind, subKey, value, onChange, previewClass }: {
  slug: string; kind: "logo" | "hero" | "module"; subKey?: string;
  value: string | null; onChange: (url: string | null) => void; previewClass?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (file: File) => {
    if (!slug) return toast.error("Save the tenant slug first.");
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file.");
    setBusy(true);
    try {
      const url = await uploadBrandAsset(slug, kind, file, { subKey });
      onChange(url); toast.success("Uploaded & optimized");
    } catch (e) { toast.error("Upload failed", { description: (e as Error).message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <div className={`grid shrink-0 place-items-center overflow-hidden bg-muted ${previewClass ?? "h-14 w-14 rounded-md border"}`}>
        {value
          ? <img src={value} alt="preview" className="h-full w-full object-cover" />
          : <Upload className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
            <Upload className="mr-1 h-3 w-3" />{busy ? "Uploading…" : value ? "Replace" : "Upload"}
          </Button>
          {value && <Button size="sm" variant="ghost" onClick={() => onChange(null)}><Trash2 className="h-3 w-3" /></Button>}
        </div>
        <Input value={value ?? ""} placeholder="or paste URL…" className="h-7 text-[11px]"
          onChange={(e) => onChange(e.target.value || null)} />
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ""; }} />
    </div>
  );
}

/* domain chip editor (kept for potential inline use) */
export function DomainChips({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [d, setD] = useState("");
  const add = () => {
    const clean = d.trim().toLowerCase();
    if (!clean) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(clean)) return toast.error("Enter a valid hostname");
    if (value.includes(clean)) return;
    onChange([...value, clean]); setD("");
  };
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((x) => (
          <Badge key={x} variant="secondary" className="gap-1">
            <Globe className="h-3 w-3" />{x}
            <button className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => onChange(value.filter((v) => v !== x))}><X className="h-3 w-3" /></button>
          </Badge>
        ))}
        {value.length === 0 && <div className="text-[11px] text-muted-foreground">No custom domains yet.</div>}
      </div>
      <div className="flex gap-2">
        <Input value={d} placeholder="e.g. portal.stmarys.ac.ug" onChange={(e) => setD(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button size="sm" variant="outline" onClick={add}><Plus className="mr-1 h-3 w-3" />Add</Button>
      </div>
    </div>
  );
}
